import type { PostgrestError } from "@supabase/supabase-js";

export const DEFAULT_PAGE_SIZE = 1000;

export type PageOpts = {
  limit?: number; // undefined => hardLimit
  pageSize?: number; // default 1000
  hardLimit: number; // safety cap
};

type QueryResult<T> = { data: T[] | null; error: PostgrestError | null };

/**
 * Supabase query builders are "thenable" (PromiseLike) and execute on await.
 * .range() returns another builder (not a Promise).
 */
export type RangeableThenableQuery<T> = PromiseLike<QueryResult<T>> & {
  range: (from: number, to: number) => RangeableThenableQuery<T>;
};

function clampLimit(limit: number | undefined, hardLimit: number) {
  const v = limit ?? hardLimit;
  if (!Number.isFinite(v)) return hardLimit;
  return Math.max(0, Math.min(Math.floor(v), hardLimit));
}

/**
 * Fetch up to `limit` rows, using one request if <= pageSize, otherwise paging with .range().
 *
 * `makeQuery` must return a *fresh* query each time, including filters + stable ordering.
 */
export async function fetchPaged<T>(
  makeQuery: () => RangeableThenableQuery<T>,
  opts: PageOpts,
): Promise<T[]> {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const target = clampLimit(opts.limit, opts.hardLimit);

  if (target === 0) return [];

  // Fast path
  if (target <= pageSize) {
    const { data, error } = await makeQuery().range(0, target - 1);
    if (error) throw error;
    return data ?? [];
  }

  // Paged path
  const out: T[] = [];
  let offset = 0;

  while (out.length < target) {
    const pageCount = Math.min(pageSize, target - out.length);
    const { data, error } = await makeQuery().range(
      offset,
      offset + pageCount - 1,
    );
    if (error) throw error;

    const page = data ?? [];
    if (page.length === 0) break;

    out.push(...page);

    if (page.length < pageCount) break;

    offset += pageCount;
  }

  return out;
}
